// =============================================================================
// MeuDriver — Termos de Uso (rota pública /termos)
// -----------------------------------------------------------------------------
// Estrutura base compatível com marketplace de serviços educacionais e LGPD.
// O texto aqui é um esqueleto pragmático — submetam à revisão jurídica antes
// de publicar em produção e atualizem a data de vigência ao final.
// =============================================================================

import { useSEO } from '@/hooks/useSEO'

const VIGENCIA = '21 de maio de 2026'

export default function TermsPage() {
  useSEO({
    title: 'Termos de Uso',
    description:
      'Termos de Uso do MeuDriver — marketplace que conecta alunos e instrutores de direção particulares.',
  })

  return (
    <div className="bg-white min-h-screen">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose prose-gray prose-sm sm:prose-base">
        <header className="not-prose mb-8 space-y-2 border-b border-gray-100 pb-6">
          <h1 className="text-3xl font-bold text-gray-900">Termos de Uso</h1>
          <p className="text-sm text-gray-500">
            Vigência: {VIGENCIA}. Esta versão pode ser atualizada;
            comunicaremos mudanças relevantes via e-mail e nesta página.
          </p>
        </header>

        <h2>1. Objeto</h2>
        <p>
          O <strong>MeuDriver</strong> ("Plataforma") é um marketplace digital
          que conecta alunos interessados em aulas de direção particular a
          instrutores autônomos previamente cadastrados. A Plataforma atua como
          intermediária — não presta diretamente o serviço de instrução nem
          emprega os instrutores.
        </p>

        <h2>2. Cadastro e elegibilidade</h2>
        <ul>
          <li>
            <strong>Aluno:</strong> deve ser maior de idade ou estar autorizado
            por responsável legal; possuir CPF válido; fornecer informações
            verdadeiras no cadastro.
          </li>
          <li>
            <strong>Instrutor:</strong> deve apresentar CNH válida com categoria
            compatível, veículo regularizado, e cumprir requisitos adicionais de
            verificação documental antes da aprovação pela equipe.
          </li>
          <li>
            Reservamos o direito de recusar, suspender ou encerrar contas que
            violem estes Termos.
          </li>
        </ul>

        <h2>3. Pagamentos e repasses</h2>
        <p>
          Pagamentos são processados via gateway parceiro (Mercado Pago). O
          valor pago pelo aluno fica <strong>retido pela Plataforma</strong>{' '}
          até a confirmação da aula. O preço pago pelo aluno é o valor da hora
          definido pelo instrutor acrescido da taxa da Plataforma vigente no
          momento da contratação; o instrutor recebe o valor por ele definido,
          após o prazo de clearing previsto na política operacional.
        </p>

        <h2>4. Cancelamento e reembolso</h2>
        <p>
          A política completa está em{' '}
          <a href="/politica-cancelamento">/politica-cancelamento</a>. Em
          resumo:
        </p>
        <ul>
          <li>
            Os pagamentos de aulas e pacotes são feitos com saldo da carteira do
            aluno, adquirido por Pix. O saldo não é reembolsável em dinheiro e só
            pode ser usado na Plataforma, ressalvado o direito de arrependimento
            previsto em lei.
          </li>
          <li>
            Ao agendar, o valor da aula fica reservado no saldo e é repassado ao
            instrutor quando a aula é realizada.
          </li>
          <li>
            Cancelamento pelo aluno com 2 horas ou mais de antecedência, ou
            cancelamento pelo instrutor: o valor volta integralmente ao saldo.
          </li>
          <li>
            Cancelamento pelo aluno com menos de 2 horas, ou não comparecimento
            do aluno: a aula é considerada realizada e o valor é repassado ao
            instrutor.
          </li>
        </ul>

        <h2>5. Responsabilidades</h2>
        <p>
          A Plataforma é mera intermediadora. O instrutor é responsável pela
          qualidade da aula, segurança do veículo e cumprimento das normas de
          trânsito. O aluno é responsável por comparecer pontualmente e
          respeitar instrutor e veículo. Disputas serão analisadas pela equipe
          de suporte conforme procedimento descrito em /ajuda.
        </p>

        <h2>6. Conduta proibida</h2>
        <ul>
          <li>Usar a Plataforma para finalidades ilegais ou fraudulentas.</li>
          <li>
            Burlar o sistema de pagamento (combinar cobrança fora da
            Plataforma).
          </li>
          <li>Assediar, ameaçar ou discriminar outros usuários.</li>
          <li>Criar perfis falsos ou usar dados de terceiros sem autorização.</li>
        </ul>

        <h2>7. Propriedade intelectual</h2>
        <p>
          Todo conteúdo da Plataforma (marca, layout, código, textos) é de
          titularidade do MeuDriver ou de seus licenciantes. É vedada
          reprodução não autorizada.
        </p>

        <h2>8. Limitação de responsabilidade</h2>
        <p>
          Dentro dos limites permitidos por lei, a Plataforma não responde por
          danos indiretos decorrentes do uso do serviço, exceto nos casos
          previstos no Código de Defesa do Consumidor.
        </p>

        <h2>9. Alterações destes Termos</h2>
        <p>
          Podemos atualizar este documento. Alterações relevantes serão
          comunicadas por e-mail e/ou notificação na Plataforma com
          antecedência mínima de 7 dias. O uso continuado após a vigência da
          nova versão implica aceitação.
        </p>

        <h2>10. Foro</h2>
        <p>
          Fica eleito o foro da comarca da sede da empresa controladora do
          MeuDriver para dirimir questões oriundas destes Termos, renunciando
          as partes a qualquer outro, por mais privilegiado que seja.
        </p>

        <h2>11. Contato</h2>
        <p>
          Dúvidas sobre estes Termos podem ser enviadas pelo canal de suporte
          em <a href="/ajuda">/ajuda</a>.
        </p>

        <p className="text-xs text-gray-400 mt-8">
          Versão {VIGENCIA}. Pendente de revisão jurídica final antes da
          abertura ao público em produção.
        </p>
      </article>
    </div>
  )
}
